import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { 
  View, 
  Text, 
  FlatList, 
  TextInput, 
  TouchableOpacity, 
  Pressable,
  StyleSheet,
  Platform,
  Modal,
  ScrollView,
} from 'react-native';
import { MessageBubble, FileBubble } from './MessageBubble';
import { LoadingSpinner } from '@/components';
import { FileUploadSection, UploadFile } from '@/components/common/admin/mobile/FileUploadSection';
import { useConversation, useConversationUtils } from '@/hooks/admin/useMessages';
import { useSendMessageMutation } from '@/store/api/admin/messagesApi';
import { IChatWindowProps, IMessage } from '@/types/admin/messages';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { PERFORMANCE_CONSTANTS } from '@/constants/admin/performance';
import { generateMessageGroupKey, generateFileKey, validateUniqueKeys } from '@/utils/admin/keyUtils';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { groupMessages, MessageGroup } from '@/utils/residents/messages/groupMessages';
import { useAutoScrollToBottom } from '@/hooks/residents/useAutoScrollToBottom';

export const ChatWindow: React.FC<IChatWindowProps> = ({
  conversationId,
}) => {
  const { conversation, isLoading, error } = useConversation(conversationId);
  const { formatMessageDate } = useConversationUtils();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  
  const [messageText, setMessageText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<UploadFile[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const { showApiError } = useErrorToast();
  
  // Use the shared auto-scroll hook for keyboard-aware scrolling
  const { 
    listRef: flatListRef, 
    isNearBottomRef, 
    maybeScrollToBottom, 
    handleScroll,
  } = useAutoScrollToBottom<FlatList>();

  // Group messages by date using the shared utility
  const messageGroups = useMemo(() => {
    const result = groupMessages(conversation?.messages, {
      getHeaderDate: (msg) => formatMessageDate(msg.createdAt),
      getTimestamp: (msg) => msg.createdAt,
    }) as MessageGroup<IMessage>[];

    // Validate unique keys in development
    validateUniqueKeys(
      result, 
      (item) => generateMessageGroupKey(item.date, item.messages.length),
      'messageGroups'
    );

    return result;
  }, [conversation?.messages, formatMessageDate]);

  // Auto-scroll to bottom when new messages arrive
  // Keyboard events are handled by the useAutoScrollToBottom hook
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (messageGroups.length > 0) {
      timeoutId = setTimeout(() => {
        maybeScrollToBottom(true);
      }, 100);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [messageGroups, maybeScrollToBottom]);

  const handleSendMessage = async () => {
    if (!messageText.trim() && attachedFiles.length === 0) return;
    
    const formData = new FormData();
    formData.append('content', messageText.trim());
    formData.append('isAdmin', 'true');
    
    // Add files to form data (UploadFile format)
    // On web, file.file contains the actual File object
    // On native, we need { uri, type, name } format
    attachedFiles.forEach((file) => {
      if (file.file) {
        // Web: use the actual File object (already contains filename)
        formData.append('files', file.file);
      } else if (file.uri) {
        // Native: use the { uri, type, name } format
        formData.append('files', {
          uri: file.uri,
          type: file.type,
          name: file.name,
        } as any);
      }
    });

    try {
      await sendMessage({
        conversationId,
        body: formData,
      }).unwrap();
      setMessageText('');
      setAttachedFiles([]);
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: false });
      }, 100);
    } catch (error: any) {
      showApiError(error?.data?.message || 'Failed to send message');
    }
  };

  const renderMessageGroup = useCallback(({ item }: { item: MessageGroup<IMessage> }) => (
    <View style={styles.messageGroup}>
      {/* Date header */}
      <View style={styles.dateHeader}>
        <Text style={styles.dateText}>{item.date}</Text>
      </View>
      
      {/* Messages */}
      {item.messages.map((message) => (
        <View key={message.id}>
          {/* Text message */}
          {message.content?.length > 0 && (
            <MessageBubble message={message} />
          )}
          
          {/* File attachments */}
          {message.files?.map((file, index) => (
            <FileBubble
              key={generateFileKey(message.id, file, index)}
              file={file}
              isAdmin={message.isAdmin}
            />
          ))}
        </View>
      ))}
    </View>
  ), []);

  const renderMessageInput = useCallback(() => (
    <View style={styles.inputContainer}>
      {/* File attachments preview */}
      {attachedFiles.length > 0 && (
        <View style={styles.attachmentsPreview}>
          {attachedFiles.map((file, index) => (
            <View key={file.id || index} style={styles.attachmentItem}>
              <SVG_COMPONENTS.PAPERCLIP
                width={16}
                height={16}
                color={ADMIN_COLORS.TEXT_SECONDARY}
              />
              <Text style={styles.attachmentName}>
                {file.name || 'File'}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setAttachedFiles(files => files.filter((_, i) => i !== index));
                }}
                style={styles.removeAttachment}
              >
                <SVG_COMPONENTS.X_CIRCLE
                  width={16}
                  height={16}
                  color={ADMIN_COLORS.ERROR}
                />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
      
      {/* Input row */}
      <View style={styles.inputRow}>
        <TouchableOpacity
          style={styles.attachButton}
          onPress={() => setShowFileUpload(true)}
        >
          <SVG_COMPONENTS.PAPERCLIP
            width={20}
            height={20}
            color={ADMIN_COLORS.TEXT_SECONDARY}
          />
        </TouchableOpacity>
        
        <TextInput
          style={styles.textInput}
          value={messageText}
          onChangeText={setMessageText}
          placeholder="Your message..."
          placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
          multiline
          maxLength={1000}
          autoFocus={false}
          blurOnSubmit={false}
          returnKeyType="default"
          keyboardType="default"
          textAlignVertical="center"
          onFocus={() => {
            // Scroll to bottom when TextInput is focused
            setTimeout(() => {
              flatListRef.current?.scrollToEnd({ animated: false });
            }, 300); // Slightly longer delay to account for keyboard animation
          }}
        />
        
        <TouchableOpacity
          style={[
            styles.sendButton,
            (messageText.trim() || attachedFiles.length > 0) && styles.sendButtonActive,
          ]}
          onPress={handleSendMessage}
          disabled={isSending || (!messageText.trim() && attachedFiles.length === 0)}
        >
          {isSending ? (
            <LoadingSpinner size="small" />
          ) : (
            <Text style={styles.sendButtonText}>Send</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  ), [attachedFiles, isSending, messageText]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingSpinner message="Loading conversation..." />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <SVG_COMPONENTS.X_CIRCLE
          width={48}
          height={48}
          color={ADMIN_COLORS.ERROR}
        />
        <Text style={styles.errorText}>
          Failed to load conversation
        </Text>
      </View>
    );
  }

  if (!conversation) {
    return (
      <View style={styles.emptyContainer}>
        <SVG_COMPONENTS.EMPTY_MESSAGE
          width={64}
          height={64}
          color={ADMIN_COLORS.TEXT_TERTIARY}
        />
        <Text style={styles.emptyText}>
          Select a conversation to start messaging
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        ref={flatListRef}
        data={messageGroups}
        renderItem={renderMessageGroup}
        keyExtractor={(item) => generateMessageGroupKey(item.date, item.messages.length)}
        style={styles.messagesList}
        contentContainerStyle={styles.messagesContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="none"
        scrollEventThrottle={16}
        // Performance optimizations
        removeClippedSubviews={true}
        maxToRenderPerBatch={PERFORMANCE_CONSTANTS.MAX_TO_RENDER_PER_BATCH}
        windowSize={PERFORMANCE_CONSTANTS.WINDOW_SIZE}
        initialNumToRender={PERFORMANCE_CONSTANTS.INITIAL_NUM_TO_RENDER}
        updateCellsBatchingPeriod={100}
        onScroll={(e) => {
          const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent;
          handleScroll(contentOffset.y, layoutMeasurement.height, contentSize.height, 24);
        }}
        onContentSizeChange={() => {
          maybeScrollToBottom(true);
        }}
        onLayout={() => {
          // Scroll to bottom when layout changes (e.g., keyboard appears)
          setTimeout(() => {
            maybeScrollToBottom(true);
          }, 100);
        }}
      />
      {renderMessageInput()}

      {/* File Upload Modal - Desktop */}
      {Platform.OS === 'web' && showFileUpload && (
        <Pressable 
          style={styles.desktopModalOverlay} 
          onPress={() => setShowFileUpload(false)}
        >
          <Pressable 
            style={styles.desktopModalContent}
            onPress={(e) => e.stopPropagation()} // Prevent closing when clicking inside
          >
            <View style={styles.desktopModalHeader}>
              <Text style={styles.desktopModalTitle}>Add Attachments</Text>
              <TouchableOpacity
                style={styles.desktopModalCloseButton}
                onPress={() => setShowFileUpload(false)}
              >
                <SVG_COMPONENTS.X_CIRCLE
                  width={24}
                  height={24}
                  color={ADMIN_COLORS.TEXT_SECONDARY}
                />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.desktopModalBody}>
              <FileUploadSection
                title="Attachments"
                subtitle="Add files to your message"
                files={attachedFiles}
                onFilesChange={setAttachedFiles}
                maxFiles={5}
                allowedTypes={['image', 'pdf', 'document', 'text']}
              />
            </ScrollView>
            
            <View style={styles.desktopModalFooter}>
              <TouchableOpacity
                style={styles.desktopModalCancelButton}
                onPress={() => setShowFileUpload(false)}
              >
                <Text style={styles.desktopModalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.desktopModalDoneButton}
                onPress={() => setShowFileUpload(false)}
              >
                <Text style={styles.desktopModalDoneText}>Done</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      )}

      {/* File Upload Modal - Mobile */}
      {Platform.OS !== 'web' && (
        <Modal
          visible={showFileUpload}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => setShowFileUpload(false)}
        >
          <View style={styles.fileUploadModal}>
            <View style={styles.fileUploadHeader}>
              <TouchableOpacity
                style={styles.fileUploadBackButton}
                onPress={() => setShowFileUpload(false)}
              >
                <SVG_COMPONENTS.CHEVRON_LEFT
                  width={24}
                  height={24}
                  color={ADMIN_COLORS.TEXT.PRIMARY}
                />
              </TouchableOpacity>
              
              <Text style={styles.fileUploadTitle}>Add Attachments</Text>
              
              <TouchableOpacity
                style={styles.fileUploadDoneButton}
                onPress={() => setShowFileUpload(false)}
              >
                <Text style={styles.fileUploadDoneText}>Done</Text>
              </TouchableOpacity>
            </View>
            
            <FileUploadSection
              title="Attachments"
              subtitle="Add files to your message"
              files={attachedFiles}
              onFilesChange={setAttachedFiles}
              maxFiles={5}
              allowedTypes={['image', 'pdf', 'document', 'text']}
            />
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 12,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  emptyText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 16,
    textAlign: 'center',
  },
  messagesList: {
    flex: 1,
  },
  messagesContent: {
    paddingVertical: 16,
    paddingBottom: 20, // Extra padding at bottom for better keyboard interaction
  },
  messageGroup: {
    marginBottom: 16,
  },
  dateHeader: {
    alignItems: 'center',
    marginVertical: 16,
  },
  dateText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  inputContainer: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  attachmentsPreview: {
    marginBottom: 12,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 4,
  },
  attachmentName: {
    flex: 1,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginLeft: 8,
  },
  removeAttachment: {
    marginLeft: 8,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  attachButton: {
    padding: 8,
    marginRight: 8,
    marginBottom: 4,
  },
  textInput: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: Platform.OS === 'ios' ? 12 : 8,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    height: 44, // Match send button height for perfect alignment
    maxHeight: 100,
    marginRight: 8,
    textAlignVertical: 'center', // Ensure text is vertically centered
    includeFontPadding: false,
  },
  sendButton: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    paddingHorizontal: 16,
    borderRadius: 20,
    minWidth: 60,
    height: 44, // Fixed height to match text input
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  sendButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  // File Upload Modal Styles
  fileUploadModal: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  fileUploadHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    paddingTop: 20, // Account for status bar
  },
  fileUploadBackButton: {
    padding: 8,
    marginRight: 8,
  },
  fileUploadTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    textAlign: 'center',
  },
  fileUploadDoneButton: {
    padding: 8,
    marginLeft: 8,
  },
  fileUploadDoneText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.PRIMARY,
  },
  // Desktop Modal Styles
  desktopModalOverlay: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  desktopModalContent: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 10,
    overflow: 'hidden',
  },
  desktopModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  desktopModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  desktopModalCloseButton: {
    padding: 4,
  },
  desktopModalBody: {
    padding: 20,
    maxHeight: 400,
  },
  desktopModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    gap: 12,
  },
  desktopModalCancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  desktopModalCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  desktopModalDoneButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  desktopModalDoneText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
